import { useEffect, useState } from "react";
import { IS_VATAR } from "../vatar/env";
import VatarShop from "../vatar/VatarShop";
import { Loader2 } from "lucide-react";
import useCoinStore from "../stores/coinStore";
import { payForProduct } from "../Services/paymentService";
import usePaymentCurrency from "../hooks/usePaymentCurrency";
import usePayerDetails from "../hooks/usePayerDetails";
import { PRODUCTS, formatProductPrice } from "../Services/products";
import BottomSheet from "./BottomSheet";
import PayerDetails from "./payment/PayerDetails";
import CurrencyToggle from "./payment/CurrencyToggle";
import LoginModal from "./LoginModal";
import AccountUpgradeModal from "./AccountUpgradeModal";
import { MascotAvatar } from "./Mascot";
import authService from "../Services/authService";

// Each line completes "You get ...".
const PERKS = [
  "Rooms for up to 10 players",
  "Unlimited room hosting",
  "200 coins every month",
  "Free in-game hints every round",
  "Unlimited dictionary checks",
];

const PLANS = {
  monthly: { productId: "pro_monthly", name: "Monthly", detail: "Renews every month. Cancel any time." },
  onetime: { productId: "pro_onetime", name: "1-month pass", detail: "One payment. Doesn't renew." },
};

function priceIn(productId, currency) {
  const product = PRODUCTS[productId];
  return currency === "USD" ? product.priceUsdCents : product.priceNgn;
}

function formatDate(value) {
  const date = value?.toDate ? value.toDate() : value?.seconds ? new Date(value.seconds * 1000) : value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })
    : null;
}

// NameIt! Pro: perks, a monthly or 1-month choice, and one pay button.
const SubscriptionModal = ({ isOpen, onClose }) => {
  const [plan, setPlan] = useState("monthly");
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const [welcome, setWelcome] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [authModal, setAuthModal] = useState(null); // "signup" | "login" | null
  const authModalOpen = !!authModal;
  // Pro lives on an account, so guests sign in first (like buying coins).
  const payer = usePayerDetails(isOpen && isSignedIn);
  const { isPro, proExpiry } = useCoinStore();
  const [currency, setCurrency] = usePaymentCurrency();

  useEffect(() => {
    if (!isOpen) return;
    setPlan("monthly");
    setError("");
    setPaying(false);
    setWelcome(false);
    setAuthModal(null);
    const user = authService.auth.currentUser;
    setIsSignedIn(!!(user && !user.isAnonymous));
  }, [isOpen]);

  const chosen = PLANS[plan];
  const price = formatProductPrice(chosen.productId, currency);
  // How much cheaper a month is on the subscription than on the pass.
  const monthlySaving = Math.round(
    (1 - priceIn("pro_monthly", currency) / priceIn("pro_onetime", currency)) * 100
  );

  const openAuth = (next) => setAuthModal(next);
  // Back on the sheet with the same plan chosen; the next tap is the payment.
  const signedIn = () => {
    setAuthModal(null);
    setIsSignedIn(true);
  };

  const close = () => {
    if (!paying && !authModalOpen) onClose();
  };

  const pay = async () => {
    if (paying) return;
    setError("");
    const details = await payer.commit();
    if (!details) return;
    setPaying(true);
    // This navigates to its hosted checkout; the webhook switches Pro on
    // once they've paid, and the status banner confirms it when they return.
    try {
      await payForProduct({ currency, productId: chosen.productId, email: details.email, name: details.name });
    } catch (err) {
      if (!err.cancelled) setError(err.message || "We couldn't start the payment. Please try again.");
      setPaying(false);
    }
  };

  // The default Namey, celebrating, beside the title (a pencil is tall and thin,
  // so stacking it above would push the plans off the screen).
  const hero = (
    <div className="flex items-center gap-3 pt-8 pb-5">
      <div className="flex-shrink-0 w-12 flex justify-center">
        <MascotAvatar mood="celebrate" size={44} />
      </div>
      <div className="min-w-0">
        <h2 className="text-2xl font-extrabold tracking-tight text-brand-text leading-7">NameIt! Pro</h2>
        <p className="mt-1 text-sm leading-5 text-brand-textSecondary whitespace-nowrap">You're the host. Now host without limits.</p>
      </div>
    </div>
  );

  let body;
  if (welcome) {
    body = (
      <div className="pb-2">
        {hero}
        <p className="text-lg font-bold text-brand-text text-center">Welcome to Pro!</p>
        <p className="mt-1 text-sm text-brand-textSecondary text-center">
          {plan === "onetime" ? "Every perk is on for the next month." : "Every perk is on. Enjoy hosting."}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="mt-5 w-full h-12 rounded-xl bg-brand-accent text-white font-semibold hover:bg-red-700 transition-colors"
        >
          Let's play
        </button>
      </div>
    );
  } else if (isPro) {
    const until = formatDate(proExpiry);
    body = (
      <div className="pb-2">
        {hero}
        <p className="text-lg font-bold text-brand-text text-center">You're already Pro</p>
        <p className="mt-1 text-sm text-brand-textSecondary text-center">
          Every perk is on{until ? ` until ${until}` : ""}.
        </p>
        <button
          type="button"
          onClick={onClose}
          className="mt-5 w-full h-12 rounded-xl border border-brand-border text-brand-text font-semibold hover:bg-brand-surface2 transition-colors"
        >
          Got it
        </button>
      </div>
    );
  } else {
    // Guests see the same sheet, prices included. Tapping the button asks them
    // to sign up (or sign in) first, then they land back here with the same plan.
    const payLabel = paying ? (
      <>
        <Loader2 size={18} className="animate-spin" aria-hidden="true" />
        Opening checkout…
      </>
    ) : plan === "monthly" ? (
      `Start Pro · ${price}/month`
    ) : (
      `Get 1 month · ${price}`
    );

    body = (
      <>
        {hero}

        <div className="rounded-2xl bg-brand-surface2 px-4 py-3">
          <p className="text-sm font-bold text-brand-text">You get</p>
          <ul className="mt-1.5 space-y-1.5">
            {PERKS.map((title) => (
              <li key={title} className="flex items-start gap-3 text-[15px] leading-5 text-brand-text">
                <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-brand-accent flex-shrink-0" aria-hidden="true" />
                {title}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm font-semibold text-brand-text">Choose your plan</p>
          <CurrencyToggle currency={currency} onChange={setCurrency} disabled={paying} />
        </div>

        {/* Both plans are visible at once; a short screen never hides one. */}
        <div role="radiogroup" aria-label="Pro plans" className="mt-4 grid grid-cols-2 gap-2.5">
          {Object.entries(PLANS).map(([key, option]) => {
            const selected = plan === key;
            return (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={paying}
                onClick={() => setPlan(key)}
                className={`relative flex flex-col text-left px-3.5 pt-3 pb-3 rounded-xl border transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent ${
                  selected ? "border-brand-accent bg-brand-accentMuted shadow-[inset_0_0_0_1px_#dc2626]" : "border-brand-border bg-brand-surface hover:bg-brand-surface2"
                }`}
              >
                {key === "monthly" && monthlySaving > 0 && (
                  <span className="absolute -top-2.5 right-3 text-[11px] font-semibold text-brand-accent bg-brand-surface border border-red-200 rounded-full px-2 py-px">
                    Save {monthlySaving}%
                  </span>
                )}
                <span className="flex items-center gap-2">
                  <span
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected ? "border-brand-accent" : "border-brand-border"}`}
                    aria-hidden="true"
                  >
                    {selected && <span className="w-2 h-2 rounded-full bg-brand-accent" />}
                  </span>
                  <span className="font-bold text-brand-text text-sm">{option.name}</span>
                </span>
                <span className="mt-2 flex items-baseline gap-1">
                  <span className="text-xl font-extrabold text-brand-text tabular-nums">{formatProductPrice(option.productId, currency)}</span>
                  {key === "monthly" && <span className="text-xs text-brand-textSecondary">/month</span>}
                </span>
                <span className="mt-1 text-xs leading-4 text-brand-textSecondary">{option.detail}</span>
              </button>
            );
          })}
        </div>

        <div className={`${payer.editing ? "" : "sticky bottom-0 "}-mx-5 -mb-5 mt-5 px-5 pt-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] bg-brand-surface border-t border-brand-borderLight space-y-3`}>
          {isSignedIn && <PayerDetails payer={payer} disabled={paying} />}
          {error && (
            <p className="rounded-lg bg-brand-accentMuted px-3 py-2.5 text-sm text-red-800" role="alert">{error}</p>
          )}
          <button
            type="button"
            onClick={isSignedIn ? pay : () => openAuth("signup")}
            disabled={paying || (isSignedIn && !payer.loaded)}
            className="w-full h-12 rounded-xl bg-brand-accent text-white font-bold text-base flex items-center justify-center gap-2 transition-colors hover:bg-red-700 disabled:bg-gray-300 disabled:text-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent"
          >
            {payLabel}
          </button>
          <p className="text-center text-xs text-brand-textSecondary">Secure checkout by Bachs</p>
          {!payer.editing && (
            <span aria-hidden="true" className="!mt-0 pointer-events-none absolute inset-x-0 -top-6 h-6 bg-gradient-to-t from-brand-surface to-transparent" />
          )}
        </div>

        <LoginModal
          isOpen={authModal === "login"}
          onClose={() => openAuth(null)}
          onSuccess={signedIn}
          onSwitchToSignUp={() => openAuth("signup")}
        />
        <AccountUpgradeModal
          isOpen={authModal === "signup"}
          onClose={() => openAuth(null)}
          onSuccess={signedIn}
          onSwitchToSignIn={() => openAuth("login")}
          subtitle="Pro is saved to your account, so you keep it on any phone"
        />
      </>
    );
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={close} tall>
      {body}
    </BottomSheet>
  );
};

// Vatar builds sell the 31-day Pro pass through Vatar.purchase (VatarShop).
export default IS_VATAR ? VatarShop : SubscriptionModal;
